// Create a material use from Inventory selection: name the job, then
// attach every selected plant SKU as a member. Primary onboarding path
// so operators never have to invent uses from a blank CRUD form first.
import { useMemo, useState } from "react";
import {
  ghostBtnClass,
  mfgMdInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  createManufacturingMaterialUse,
  upsertManufacturingMaterialUseMember,
} from "@/lib/manufacturing/materialUsesClient";
import { nextMaterialUsePreferenceRank } from "@/lib/manufacturing/materialUsePreferenceRank";
import type { MfgMaterial, MfgMaterialUse } from "@/lib/manufacturing/types";
import { ManufacturingMaterialUseExplainer } from "./ManufacturingMaterialUseExplainer";

function useCodeFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug.slice(0, 48) || "use";
}

type Props = {
  open: boolean;
  materials: readonly MfgMaterial[];
  onClose: () => void;
  onCreated: (use: MfgMaterialUse, memberCount: number) => void;
};

export function ManufacturingMaterialUseFromMaterialsModal({
  open,
  materials,
  onClose,
  onCreated,
}: Props) {
  const [name, setName] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [code, setCode] = useState("");
  const [unit, setUnit] = useState("each");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plantIds = useMemo(() => new Set(materials.map((m) => m.plant_id)), [materials]);
  const mixedPlants = plantIds.size > 1;

  function resetAndClose() {
    setName("");
    setCode("");
    setCodeTouched(false);
    setUnit("each");
    setError(null);
    setBusy(false);
    onClose();
  }

  async function onSubmit() {
    const trimmedName = name.trim();
    const trimmedCode = (codeTouched ? code : useCodeFromName(name)).trim();
    if (!trimmedName || !trimmedCode || materials.length === 0) return;
    setBusy(true);
    setError(null);
    const created = await createManufacturingMaterialUse({
      use_code: trimmedCode,
      name: trimmedName,
      unit: unit.trim() || "each",
      notes: "",
    });
    if (!created.ok) {
      setBusy(false);
      setError(created.error);
      return;
    }
    let memberCount = 0;
    const failures: string[] = [];
    const assigned: { preference_rank: number; is_active: boolean }[] = [];
    for (const m of materials) {
      const preference_rank = nextMaterialUsePreferenceRank(assigned);
      const r = await upsertManufacturingMaterialUseMember({
        use_id: created.data.use.id,
        material_id: m.id,
        preference_rank,
      });
      if (r.ok) {
        memberCount += 1;
        assigned.push({ preference_rank, is_active: true });
      } else failures.push(`${m.name}: ${r.error}`);
    }
    setBusy(false);
    if (failures.length > 0 && memberCount === 0) {
      setError(failures[0] ?? "Could not add members.");
      return;
    }
    onCreated(created.data.use, memberCount);
    resetAndClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mfg-use-from-materials-title"
        className="max-h-[90vh] w-full max-w-xl overflow-auto rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="mfg-use-from-materials-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Make these interchangeable
        </h2>
        <div className="mt-3 space-y-4">
          <ManufacturingMaterialUseExplainer variant="uses-create" />
          <div>
            <p className="mb-1 text-sm font-medium text-gray-800 dark:text-zinc-200">
              Selected materials ({materials.length})
            </p>
            <ul className="max-h-36 overflow-y-auto rounded-md border border-gray-200 text-sm dark:border-zinc-700">
              {materials.map((m) => (
                <li
                  key={m.id}
                  className="border-b border-gray-100 px-2 py-1.5 last:border-b-0 dark:border-zinc-800"
                >
                  <span className="font-medium text-gray-900 dark:text-zinc-100">{m.name}</span>
                  <span className="ml-2 font-mono text-xs text-gray-500 dark:text-zinc-400">
                    {m.code}
                  </span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-zinc-400">{m.unit}</span>
                </li>
              ))}
            </ul>
            {mixedPlants ? (
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                Selection spans more than one plant. Members stay plant-scoped; at build time only
                members at the build plant are eligible.
              </p>
            ) : null}
          </div>

          <label className="block text-sm">
            <span className="font-medium text-gray-700 dark:text-zinc-300">
              What job do these fill?
            </span>
            <input
              className={mfgMdInputClass + " mt-1 w-full"}
              value={name}
              placeholder="e.g. Doorknob"
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value;
                setName(v);
                if (!codeTouched) setCode(useCodeFromName(v));
              }}
            />
            <span className="mt-1 block text-xs text-gray-500 dark:text-zinc-400">
              This is what BOM authors pick later — not a vendor SKU name.
            </span>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Use code</span>
              <input
                className={mfgMdInputClass + " mt-1 w-full font-mono"}
                value={codeTouched ? code : useCodeFromName(name)}
                disabled={busy}
                onChange={(e) => {
                  setCodeTouched(true);
                  setCode(e.target.value);
                }}
              />
              <span className="mt-1 block text-xs text-gray-500 dark:text-zinc-400">
                Stable internal id (auto from the job name).
              </span>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-zinc-300">BOM unit</span>
              <input
                className={mfgMdInputClass + " mt-1 w-full"}
                value={unit}
                disabled={busy}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="each"
              />
              <span className="mt-1 block text-xs text-gray-500 dark:text-zinc-400">
                How the model counts this job (usually each, sheet, lf…).
              </span>
            </label>
          </div>

          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={busy || !name.trim() || materials.length === 0}
              onClick={() => void onSubmit()}
            >
              {busy ? "Creating…" : "Create use + add members"}
            </button>
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={resetAndClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
